// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 19-design-system.md
//
// Per-app integration permission grants/requests. This view is read-only on the
// Access tab — granting/denying/revoking lives on /permissions. The IT-only Manage
// link directs there. Status filter chips (All/Pending/Granted/Denied/Revoked)
// are surfaced only to users who can manage grants (same gate as the Manage link).
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { AD_GROUP, AD_ROW } from '@/components/greenlight/surfaces';
import { cn } from '@/lib/utils';
import { HeldReason } from '@/components/permissions/HeldReason';
import { FilterPill } from '@/components/greenlight/FilterPill';
import { api } from '@/lib/api';
import type { PermissionStatus } from '@greenlight/shared';

import { TabErrorPanel, TabLoadingRows } from './tab-primitives';

// Inline the status array so this module doesn't force a @greenlight/shared
// runtime bundle entry (the package isn't pre-built in dev).
const PERMISSION_STATUSES = ['pending', 'granted', 'denied', 'revoked'] as const;

// ── Status badge map ─────────────────────────────────────────────────────────

const PERM_META: Record<PermissionStatus, { variant: StatusBadgeVariant; label: string }> = {
  granted: { variant: 'granted', label: 'Granted' },
  pending: { variant: 'pending', label: 'Pending' },
  denied: { variant: 'failed', label: 'Denied' },
  revoked: { variant: 'revoked', label: 'Revoked' },
};

// ── Filter chips ─────────────────────────────────────────────────────────────

// "All" is the sentinel — no filter applied.
type FilterValue = PermissionStatus | 'all';
const CHIP_LABELS: Array<{ value: FilterValue; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'granted', label: 'Granted' },
  { value: 'denied', label: 'Denied' },
  { value: 'revoked', label: 'Revoked' },
];

function isPermissionStatus(v: string | null): v is PermissionStatus {
  return PERMISSION_STATUSES.includes(v as PermissionStatus);
}

interface FilterChipsProps {
  active: FilterValue;
  onChange: (v: FilterValue) => void;
}

function FilterChips({ active, onChange }: FilterChipsProps): React.ReactElement {
  return (
    <div
      className="flex flex-wrap gap-1.5"
      role="group"
      aria-label="Filter by status"
      data-testid="perm-filter-chips"
    >
      {CHIP_LABELS.map(({ value, label }) => {
        const isActive = active === value;
        return (
          <FilterPill key={value} selected={isActive} onClick={() => onChange(value)}>
            {label}
          </FilterPill>
        );
      })}
    </div>
  );
}

// ── Empty states ─────────────────────────────────────────────────────────────

function AllEmpty(): React.ReactElement {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 py-10 text-center"
      data-testid="perm-empty-all"
    >
      <CheckCircle className="h-8 w-8 text-muted-foreground/50" aria-hidden="true" />
      <p className="text-sm font-semibold text-foreground">
        This app hasn&rsquo;t requested any permissions yet.
      </p>
      <p className="text-[13px] text-muted-foreground max-w-[30rem]">
        Grants appear here when an app&rsquo;s agent requests access to an integration.
      </p>
    </div>
  );
}

function FilteredEmpty({ status }: { status: PermissionStatus }): React.ReactElement {
  const label = PERM_META[status]?.label ?? status;
  return (
    <div
      className="flex items-center justify-center py-8 text-sm text-muted-foreground"
      data-testid="perm-empty-filtered"
    >
      No {label.toLowerCase()} requests.
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface PermissionsTabProps {
  appId: string;
  /** When true the user can manage grants: show filter chips (same gate as the Manage link). */
  canManage: boolean;
}

export function PermissionsTab({ appId, canManage }: PermissionsTabProps): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter chips persist active status in ?perm= (docs/28 filter convention).
  // Only rendered/used when canManage — otherwise the param is ignored.
  const rawPerm = searchParams.get('perm');
  const activeFilter: FilterValue = canManage && isPermissionStatus(rawPerm) ? rawPerm : 'all';

  const setFilter = React.useCallback(
    (v: FilterValue) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (v === 'all') {
            p.delete('perm');
          } else {
            p.set('perm', v);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data, isLoading, isError } = api.permissions.list.useQuery(['permissions', appId], {
    params: { id: appId },
  });

  if (isLoading) return <TabLoadingRows />;
  if (isError || !data || data.status !== 200)
    return <TabErrorPanel message="Couldn't load permissions." />;

  const items = data.body.items;
  const filtered = activeFilter === 'all' ? items : items.filter((p) => p.status === activeFilter);

  return (
    <div className="space-y-3" data-testid="permissions-tab">
      <p className="text-[13px] text-muted-foreground max-w-prose">
        Integration access this app&rsquo;s agent has requested. Approvals are written to the audit
        log; revocations take effect within ~60s.
      </p>

      {/* Filter chips only for users who can manage grants — same gate as Manage link. */}
      {canManage && <FilterChips active={activeFilter} onChange={setFilter} />}

      <div className={AD_GROUP}>
        {items.length === 0 ? (
          <AllEmpty />
        ) : filtered.length === 0 ? (
          // activeFilter is guaranteed to be a PermissionStatus here (not 'all')
          // because items.length > 0 but filtered.length === 0 implies a filter is active.
          <FilteredEmpty status={activeFilter as PermissionStatus} />
        ) : (
          <ul>
            {filtered.map((p) => {
              const meta = PERM_META[p.status as PermissionStatus] ?? {
                variant: 'inactive' as StatusBadgeVariant,
                label: p.status,
              };
              return (
                <li key={p.id} className={cn(AD_ROW, 'flex-wrap justify-between gap-2')}>
                  <div className="flex items-center gap-3 text-sm">
                    <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
                    <MonoValue>{p.credentialSlug}</MonoValue>
                    <span className="text-xs text-muted-foreground">
                      {new Date(p.requestedAt).toLocaleDateString()}
                    </span>
                  </div>
                  {p.heldReason && <HeldReason reason={p.heldReason} className="w-full" />}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
